/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
export function normalizeConversationIdentityText(value) {
  return typeof value === "string" ? value.replace(/\s+/g, " ").trim() : "";
}

/**
 * @param {{ nombre?: string, rol?: string, key?: string } | null | undefined} raw
 * @returns {{ nombre: string, rol: string, key: string }}
 */
export function buildConversationIdentity(raw) {
  const nombre = normalizeConversationIdentityText(raw?.nombre);
  const rol = normalizeConversationIdentityText(raw?.rol);
  const explicitKey = normalizeConversationIdentityText(raw?.key);
  const key = explicitKey || (nombre || rol ? `${nombre.toLowerCase()}|${rol.toLowerCase()}` : "");
  return { nombre, rol, key };
}

/**
 * @param {object | null | undefined} contacto
 * @returns {{ nombre: string, rol: string, key: string }}
 */
export function identityFromContact(contacto) {
  if (!contacto || typeof contacto !== "object") {
    return buildConversationIdentity(null);
  }
  if (contacto.identidadConversacion && typeof contacto.identidadConversacion === "object") {
    return buildConversationIdentity(contacto.identidadConversacion);
  }
  if (typeof contacto.identidadKey === "string" && contacto.identidadKey.trim() !== "") {
    return buildConversationIdentity({
      key: contacto.identidadKey,
      nombre: contacto.nombreVisibleConversacion,
      rol: contacto.rolVisibleConversacion,
    });
  }
  return buildConversationIdentity({
    nombre: contacto.nombreVisibleConversacion,
    rol: contacto.rolVisibleConversacion,
  });
}

/**
 * @param {object | null | undefined} contacto
 * @returns {string}
 */
export function conversationRowKey(contacto) {
  if (!contacto || typeof contacto !== "object") {
    return "";
  }
  const contactId = String(contacto.contactoId ?? contacto._id ?? contacto.id ?? "").trim();
  if (!contactId) return "";
  return `${contactId}::${identityFromContact(contacto).key}`;
}

/**
 * @param {object | null | undefined} left
 * @param {object | null | undefined} right
 * @returns {boolean}
 */
export function isSameConversation(left, right) {
  const leftKey = conversationRowKey(left);
  const rightKey = conversationRowKey(right);
  return Boolean(leftKey) && leftKey === rightKey;
}

/**
 * @param {string} cursoId
 * @param {string} contactoId
 * @param {string} identityKey
 * @returns {string}
 */
export function conversationCacheKey(cursoId, contactoId, identityKey) {
  return `${String(cursoId || "")}-${String(contactoId || "")}-${String(identityKey || "")}`;
}

/**
 * @param {string} template
 * @param {{ name: string, role: string }} values
 * @returns {string}
 */
export function formatConversationIdentityLabel(template, values) {
  const name = normalizeConversationIdentityText(values?.name);
  const role = normalizeConversationIdentityText(values?.role);
  if (!name && !role) return "";
  if (!template) {
    return [name, role].filter(Boolean).join(" · ");
  }
  return template.replace("{name}", name).replace("{role}", role);
}

/**
 * @param {object | null | undefined} contacto
 * @param {{ nombre?: string, rol?: string, key?: string } | null | undefined} identity
 * @param {{ viewerIsStudent?: boolean }} [options]
 * @returns {object}
 */
export function decorateContactForThread(contacto, identity, options = {}) {
  const resolved = buildConversationIdentity(identity);
  if (!contacto || typeof contacto !== "object") {
    return {
      identidadConversacion: resolved,
      identidadKey: resolved.key,
      nombreVisibleConversacion: resolved.nombre,
      rolVisibleConversacion: resolved.rol,
    };
  }
  const viewerIsStudent = options.viewerIsStudent === true;
  return {
    ...contacto,
    identidadConversacion: resolved,
    identidadKey: resolved.key,
    nombreVisibleConversacion: viewerIsStudent
      ? resolved.nombre || contacto.nombreVisibleConversacion || contacto.nombre
      : contacto.nombreVisibleConversacion || contacto.nombre,
    rolVisibleConversacion: resolved.rol || contacto.rolVisibleConversacion,
  };
}

/**
 * Staff must open a new thread from the identity they are writing as now.
 * @param {{
 *   canConfigureVisibleSender?: boolean,
 *   visibleSender?: { nombre?: string, rol?: string } | null,
 *   activeContact?: object | null,
 * }} params
 * @returns {{ nombre: string, rol: string, key: string }}
 */
export function resolveOutgoingThreadIdentity({
  canConfigureVisibleSender = false,
  visibleSender = null,
  activeContact = null,
} = {}) {
  if (canConfigureVisibleSender && visibleSender && (visibleSender.nombre || visibleSender.rol)) {
    return buildConversationIdentity(visibleSender);
  }
  return identityFromContact(activeContact);
}
